// Tooltip that only shows when content overflows (ellipsis is visible).
// Uses MUI Tooltip with overflow detection via ResizeObserver.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { tooltipStyles } from '@constants/mui-theme';

const useStyles = makeStyles(() => ({
  tooltip: {
    backgroundColor: tooltipStyles.backgroundColor,
    borderRadius: tooltipStyles.borderRadius,
    color: tooltipStyles.color,
    fontFamily: tooltipStyles.fontFamily,
    fontSize: tooltipStyles.fontSize,
    lineHeight: tooltipStyles.lineHeight,
    padding: tooltipStyles.padding
  }
}));

const OverflowTooltip = ({
  children,
  title,
  targetId,
  style,
  className,
  placement = 'bottom-start',
  ...props
}) => {
  const classes = useStyles();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef(null);

  // Get the element to check for overflow
  // If targetId is provided, find that specific element (for icon+label combos)
  // Otherwise use the container
  const getTargetElement = useCallback(() => {
    if (targetId) {
      return document.getElementById(`item_label_${targetId}`);
    }
    return containerRef.current;
  }, [targetId]);

  const checkOverflow = useCallback(() => {
    const el = getTargetElement();
    if (el) {
      const overflowing = el.scrollWidth > el.offsetWidth;
      setIsOverflowing(overflowing);
    }
  }, [getTargetElement]);

  useEffect(() => {
    // Check overflow on mount and when children change
    // Use requestAnimationFrame to ensure layout is complete
    const rafId = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(rafId);
  }, [children, checkOverflow]);

  useEffect(() => {
    const el = getTargetElement();
    if (!el) {
      return () => {}; // No-op cleanup
    }

    // Watch for resize changes
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [checkOverflow, getTargetElement]);

  const tooltipTitle = typeof title !== 'undefined' ? title : children;

  return (
    <Tooltip
      title={isOverflowing ? tooltipTitle : ''}
      placement={placement}
      classes={{ tooltip: classes.tooltip }}
      {...props}
    >
      <div
        ref={containerRef}
        className={className}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          minWidth: 0,
          ...style
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

OverflowTooltip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  placement: PropTypes.string,
  style: PropTypes.object,
  targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

export default OverflowTooltip;
